import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  ActionIcon,
  Box,
  Group,
  Menu,
  ScrollArea,
  Tabs,
  Text,
  createStyles,
} from "@mantine/core";
import {
  IconArrowBarLeft,
  IconArrowBarRight,
  IconArrowsDiff,
  IconChevronLeft,
  IconChevronRight,
  IconExternalLink,
  IconPin,
  IconRefresh,
  IconX,
} from "@tabler/icons";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TabRouter from "./tabRouter";
import { AppRouteTree } from "../routes/appRoutes";
import { useTabActions, useTabState } from "../context/tabContext";
import { buildTabLocation, TAB_BAR_STICKY_HEIGHT } from "../utils/tabNavigation";
import { restoreTabScroll } from "../utils/tabScroll";

const SCROLL_EDGE_THRESHOLD = 1;

const useStyles = createStyles((theme) => ({
  tabShell: {
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 60px)",
  },
  tabListWrapper: {
    backgroundColor:
      theme.colorScheme === "dark" ? theme.colors.dark[8] : theme.colors.gray[0],
    borderBottom: `1px solid ${
      theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[4]
    }`,
    position: "sticky",
    top: 0,
    zIndex: 150,
    flexShrink: 0,
    padding: "1px 4px 0",
  },
  scrollControlsGroup: {
    width: "100%",
    alignItems: "stretch",
  },
  scrollButton: {
    flexShrink: 0,
    height: TAB_BAR_STICKY_HEIGHT,
    width: 22,
    borderRadius: 0,
    color:
      theme.colorScheme === "dark"
        ? theme.colors.dark[1]
        : theme.colors.gray[7],
    "&:disabled": {
      opacity: 0.35,
      backgroundColor: "transparent",
    },
  },
  scrollButtonLeft: {
    borderRight: `1px solid ${
      theme.colorScheme === "dark"
        ? theme.colors.dark[4]
        : theme.colors.gray[4]
    }`,
  },
  scrollButtonRight: {
    borderLeft: `1px solid ${
      theme.colorScheme === "dark"
        ? theme.colors.dark[4]
        : theme.colors.gray[4]
    }`,
  },
  scrollViewport: {
    paddingBottom: 2,
  },
  tabsList: {
    flexWrap: "nowrap",
    gap: 0,
    borderBottom: "none",
    backgroundColor: "transparent",
  },
  tab: {
    border: "none",
    borderRadius: theme.radius.sm,
    backgroundColor: "transparent",
    padding: "1px 10px",
    height: "auto",
    minHeight: "unset",
    lineHeight: 1.2,
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    color:
      theme.colorScheme === "dark"
        ? theme.colors.dark[1]
        : theme.colors.gray[7],
    "&:not(:first-of-type)": {
      borderLeft: `1px solid ${
        theme.colorScheme === "dark"
          ? theme.colors.dark[4]
          : theme.colors.gray[4]
      }`,
    },
    "&[data-active]": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[5]
          : theme.white,
      color:
        theme.colorScheme === "dark" ? theme.white : theme.black,
      boxShadow:
        theme.colorScheme === "dark"
          ? undefined
          : "0 1px 3px rgba(0, 0, 0, 0.08)",
      borderLeftColor: "transparent !important",
      "& + &": {
        borderLeftColor: "transparent !important",
      },
      "& .mantine-Text-root, & .mantine-ActionIcon-root": {
        color: "inherit",
        fontWeight: 500,
      },
      "&:hover": {
        backgroundColor:
          theme.colorScheme === "dark"
            ? theme.colors.dark[5]
            : theme.white,
        color:
          theme.colorScheme === "dark" ? theme.white : theme.black,
      },
    },
    "&:hover:not([data-active])": {
      backgroundColor: "transparent",
      color:
        theme.colorScheme === "dark" ? theme.colors.dark[0] : theme.black,
    },
  },
  tabLabel: {
    maxWidth: 180,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  panel: {
    flex: 1,
    minHeight: 0,
    position: "relative",
    zIndex: 1,
    paddingTop: theme.spacing.md,
  },
}));

function TabLocationSync({ tabId }) {
  const location = useLocation();
  const { updateTabLocation } = useTabActions();
  const { activeTabId } = useTabState();

  useEffect(() => {
    if (tabId !== activeTabId) return;
    updateTabLocation(tabId, location.pathname, location.search);
  }, [
    tabId,
    activeTabId,
    location.pathname,
    location.search,
    updateTabLocation,
  ]);

  return null;
}

const TabPanel = memo(function TabPanel({ tab }) {
  const initialEntry = buildTabLocation(tab.pathname, tab.search);

  return (
    <TabRouter initialEntry={initialEntry}>
      <TabLocationSync tabId={tab.id} />
      <AppRouteTree />
    </TabRouter>
  );
});

function TabContextMenu({
  tab,
  tabs,
  opened,
  onOpen,
  onClose,
  onRefresh,
  onCloseTab,
  onCloseLeft,
  onCloseRight,
  onCloseOther,
  onOpenInNewWindow,
  onPin,
  onUnpin,
  classes,
  t,
  closeTab,
}) {
  const isHomeTab = tab.id === "home-tab";
  const isUserPinned = tab.pinned && !isHomeTab;
  const tabIndex = tabs.findIndex((item) => item.id === tab.id);
  const hasClosableLeft = tabs
    .slice(0, tabIndex)
    .some((item) => item.closable);
  const hasClosableRight = tabs
    .slice(tabIndex + 1)
    .some((item) => item.closable);
  const hasOtherClosable = tabs.some(
    (item) => item.id !== tab.id && item.closable
  );

  return (
    <Menu
      opened={opened}
      onChange={(nextOpened) => {
        if (!nextOpened) onClose();
      }}
      position="bottom-start"
      withinPortal
    >
      <Menu.Target>
        <Tabs.Tab
          value={tab.id}
          onContextMenu={(event) => {
            event.preventDefault();
            onOpen();
          }}
          onMouseDown={(event) => {
            if (event.button === 1 && tab.closable) {
              event.preventDefault();
              closeTab(tab.id);
            }
          }}
        >
          <Group spacing={4} noWrap>
            {isUserPinned && (
              <ActionIcon
                component="span"
                size={16}
                variant="transparent"
                title={t("Unpin")}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onUnpin();
                }}
              >
                <IconPin size={11} />
              </ActionIcon>
            )}
            <Text size="sm" className={classes.tabLabel}>
              {t(tab.label)}
            </Text>
            {tab.closable && (
              <ActionIcon
                // Tabs.Tab is already a button; a nested ActionIcon button trips validateDOMNesting.
                component="span"
                size={16}
                variant="transparent"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  closeTab(tab.id);
                }}
              >
                <IconX size={10} />
              </ActionIcon>
            )}
          </Group>
        </Tabs.Tab>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item icon={<IconRefresh size={14} />} onClick={onRefresh}>
          {t("Refresh")}
        </Menu.Item>
        {!isHomeTab && (
          <Menu.Item
            icon={<IconPin size={14} />}
            onClick={isUserPinned ? onUnpin : onPin}
          >
            {isUserPinned ? t("Unpin") : t("Pin")}
          </Menu.Item>
        )}
        <Menu.Item
          icon={<IconX size={14} />}
          onClick={onCloseTab}
          disabled={!tab.closable}
        >
          {t("Close")}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          icon={<IconArrowBarLeft size={14} />}
          onClick={onCloseLeft}
          disabled={!hasClosableLeft}
        >
          {t("Close Left Tabs")}
        </Menu.Item>
        <Menu.Item
          icon={<IconArrowBarRight size={14} />}
          onClick={onCloseRight}
          disabled={!hasClosableRight}
        >
          {t("Close Right Tabs")}
        </Menu.Item>
        <Menu.Item
          icon={<IconArrowsDiff size={14} />}
          onClick={onCloseOther}
          disabled={!hasOtherClosable}
        >
          {t("Close Other")}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          icon={<IconExternalLink size={14} />}
          onClick={onOpenInNewWindow}
        >
          {t("Open in new Window")}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

const TabBar = memo(function TabBar({
  tabs,
  activeTabId,
  activateTab,
  closeTab,
  closeTabsToLeft,
  closeTabsToRight,
  closeOtherTabs,
  refreshTab,
  pinTab,
  unpinTab,
  classes,
}) {
  const { t } = useTranslation();
  const viewportRef = useRef(null);
  const prevTabCountRef = useRef(tabs.length);
  const [contextTabId, setContextTabId] = useState(null);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
    showScrollControls: false,
  });

  const updateScrollState = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const { scrollLeft, scrollWidth, clientWidth } = viewport;
    const showScrollControls =
      scrollWidth > clientWidth + SCROLL_EDGE_THRESHOLD;

    setScrollState({
      canScrollLeft: scrollLeft > SCROLL_EDGE_THRESHOLD,
      canScrollRight:
        scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_THRESHOLD,
      showScrollControls,
    });
  }, []);

  const getScrollStep = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return 180;
    return Math.max(160, Math.floor(viewport.clientWidth * 0.8));
  }, []);

  const scrollBy = useCallback(
    (delta) => {
      const viewport = viewportRef.current;
      if (!viewport) return;
      viewport.scrollBy({ left: delta, behavior: "smooth" });
    },
    []
  );

  const scrollActiveTabIntoView = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const activeTab = viewport.querySelector("[data-active]");
    if (!activeTab) return;

    activeTab.scrollIntoView({
      inline: "nearest",
      block: "nearest",
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    updateScrollState();

    const handleScroll = () => updateScrollState();
    viewport.addEventListener("scroll", handleScroll, { passive: true });

    const resizeObserver = new ResizeObserver(() => updateScrollState());
    resizeObserver.observe(viewport);
    if (viewport.firstElementChild) {
      resizeObserver.observe(viewport.firstElementChild);
    }

    return () => {
      viewport.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [updateScrollState, tabs.length]);

  useEffect(() => {
    const tabCountIncreased = tabs.length > prevTabCountRef.current;
    prevTabCountRef.current = tabs.length;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const viewport = viewportRef.current;
        if (!viewport) return;

        if (tabCountIncreased) {
          viewport.scrollTo({
            left: viewport.scrollWidth,
            behavior: "smooth",
          });
        }

        updateScrollState();
      });
    });
  }, [tabs.length, updateScrollState]);

  useEffect(() => {
    requestAnimationFrame(() => {
      scrollActiveTabIntoView();
      updateScrollState();
    });
  }, [activeTabId, scrollActiveTabIntoView, updateScrollState]);

  useEffect(() => {
    if (contextTabId && !tabs.some((tab) => tab.id === contextTabId)) {
      setContextTabId(null);
    }
  }, [tabs, contextTabId]);

  const closeContextMenu = () => setContextTabId(null);
  const { canScrollLeft, canScrollRight, showScrollControls } = scrollState;

  return (
    <Box className={classes.tabListWrapper}>
      <Group spacing={0} noWrap className={classes.scrollControlsGroup}>
        {showScrollControls && (
          <ActionIcon
            className={`${classes.scrollButton} ${classes.scrollButtonLeft}`}
            size="sm"
            variant="transparent"
            disabled={!canScrollLeft}
            onClick={() => scrollBy(-getScrollStep())}
            aria-label="Scroll tabs left"
          >
            <IconChevronLeft size={14} />
          </ActionIcon>
        )}
        <ScrollArea
          type="never"
          offsetScrollbars
          viewportRef={viewportRef}
          classNames={{ viewport: classes.scrollViewport }}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Tabs
            value={activeTabId}
            onTabChange={activateTab}
            variant="pills"
            radius="sm"
            classNames={{
              tabsList: classes.tabsList,
              tab: classes.tab,
            }}
            styles={(theme) => ({
              tab: {
                paddingTop: 1,
                paddingBottom: 1,
                height: "auto",
                minHeight: "unset",
                "&[data-active]:hover": {
                  backgroundColor:
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[5]
                      : theme.white,
                },
              },
            })}
          >
            <Tabs.List>
              {tabs.map((tab) => (
                <TabContextMenu
                  key={tab.id}
                  tab={tab}
                  tabs={tabs}
                  opened={contextTabId === tab.id}
                  onOpen={() => setContextTabId(tab.id)}
                  onClose={closeContextMenu}
                  onRefresh={() => {
                    refreshTab(tab.id);
                    closeContextMenu();
                  }}
                  onCloseTab={() => {
                    closeTab(tab.id);
                    closeContextMenu();
                  }}
                  onCloseLeft={() => {
                    closeTabsToLeft(tab.id);
                    closeContextMenu();
                  }}
                  onCloseRight={() => {
                    closeTabsToRight(tab.id);
                    closeContextMenu();
                  }}
                  onCloseOther={() => {
                    closeOtherTabs(tab.id);
                    closeContextMenu();
                  }}
                  onOpenInNewWindow={() => {
                    const url = `${window.location.origin}${buildTabLocation(
                      tab.pathname,
                      tab.search
                    )}`;
                    window.open(url, "_blank");
                    closeContextMenu();
                  }}
                  onPin={() => {
                    pinTab(tab.id);
                    closeContextMenu();
                  }}
                  onUnpin={() => {
                    unpinTab(tab.id);
                    closeContextMenu();
                  }}
                  classes={classes}
                  t={t}
                  closeTab={closeTab}
                />
              ))}
            </Tabs.List>
          </Tabs>
        </ScrollArea>
        {showScrollControls && (
          <ActionIcon
            className={`${classes.scrollButton} ${classes.scrollButtonRight}`}
            size="sm"
            variant="transparent"
            disabled={!canScrollRight}
            onClick={() => scrollBy(getScrollStep())}
            aria-label="Scroll tabs right"
          >
            <IconChevronRight size={14} />
          </ActionIcon>
        )}
      </Group>
    </Box>
  );
});

export default function TabShell() {
  const { classes } = useStyles();
  const { tabs, activeTabId, tabRefreshKeys } = useTabState();
  const {
    activateTab,
    closeTab,
    closeTabsToLeft,
    closeTabsToRight,
    closeOtherTabs,
    refreshTab,
    pinTab,
    unpinTab,
  } = useTabActions();

  useLayoutEffect(() => {
    restoreTabScroll(activeTabId);
    const frame = requestAnimationFrame(() => restoreTabScroll(activeTabId));
    return () => cancelAnimationFrame(frame);
  }, [activeTabId]);

  return (
    <Box
      className={classes.tabShell}
      style={{ "--mbid-tab-bar-height": `${TAB_BAR_STICKY_HEIGHT}px` }}
    >
      <TabBar
        tabs={tabs}
        activeTabId={activeTabId}
        activateTab={activateTab}
        closeTab={closeTab}
        closeTabsToLeft={closeTabsToLeft}
        closeTabsToRight={closeTabsToRight}
        closeOtherTabs={closeOtherTabs}
        refreshTab={refreshTab}
        pinTab={pinTab}
        unpinTab={unpinTab}
        classes={classes}
      />

      <Box className={classes.panel}>
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          return (
            <div
              key={`${tab.id}-${tabRefreshKeys[tab.id] ?? 0}`}
              style={{ display: isActive ? "block" : "none" }}
              aria-hidden={!isActive}
            >
              <TabPanel tab={tab} />
            </div>
          );
        })}
      </Box>
    </Box>
  );
}
